/**
 * Admin session utilities.
 *
 * Backend issues admin JWTs with a 6h TTL. The frontend mirrors that here:
 *  - getAdminToken()        → returns the token only if its `exp` claim is still in the future
 *  - setAdminSession()      → stores the token + admin profile and arms an auto-logout timer
 *  - clearAdminSession()    → wipes the session (called on logout / expiry / 401)
 *  - initAdminSession()     → arms the timer at app boot if a valid token already exists
 *
 * The auto-logout timer is keyed off the JWT exp claim (not a wall-clock timer
 * we set ourselves) so it survives reloads and can never run longer than the
 * server-side validity.
 */

const TOKEN_KEY = 'adminToken';
const DATA_KEY = 'adminData';
const SIGNIN_PATH = '/admin/signin';

let logoutTimerId = null;
let visibilityListenerInstalled = false;

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch (_err) {
    return null;
  }
}

function tokenExpiryMs(token) {
  const decoded = decodeJwt(token);
  if (!decoded || typeof decoded.exp !== 'number') return 0;
  return decoded.exp * 1000;
}

export function getAdminToken() {
  const token = typeof localStorage !== 'undefined'
    ? localStorage.getItem(TOKEN_KEY)
    : null;
  if (!token) return null;
  const expMs = tokenExpiryMs(token);
  if (!expMs || expMs <= Date.now()) {
    clearAdminSession();
    return null;
  }
  return token;
}

export function getAdminData() {
  try {
    const raw = localStorage.getItem(DATA_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_err) {
    return {};
  }
}

export function isAdminAuthenticated() {
  return Boolean(getAdminToken());
}

export function clearAdminSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(DATA_KEY);
  } catch (_err) { /* ignore */ }
  if (logoutTimerId) {
    clearTimeout(logoutTimerId);
    logoutTimerId = null;
  }
}

function redirectToSignIn(reason) {
  if (typeof window === 'undefined') return;
  const onAdminPage = window.location.pathname.startsWith('/admin');
  if (!onAdminPage) return;
  if (window.location.pathname === SIGNIN_PATH) return;
  const qs = reason === 'expired' ? '?expired=1' : '';
  window.location.replace(`${SIGNIN_PATH}${qs}`);
}

function handleExpiry() {
  clearAdminSession();
  redirectToSignIn('expired');
}

function armTimer(token) {
  if (logoutTimerId) {
    clearTimeout(logoutTimerId);
    logoutTimerId = null;
  }
  const expMs = tokenExpiryMs(token);
  const remaining = expMs - Date.now();
  if (remaining <= 0) {
    handleExpiry();
    return;
  }
  // setTimeout truncates to a 32-bit signed int; cap so very long timers still work.
  const safeDelay = Math.min(remaining, 2_147_000_000);
  logoutTimerId = setTimeout(handleExpiry, safeDelay);
}

export function setAdminSession(token, admin) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(DATA_KEY, JSON.stringify(admin || {}));
  } catch (_err) { /* ignore quota errors */ }
  armTimer(token);
}

/**
 * Call once at app boot. Safe to call multiple times.
 *  - Re-arms the auto-logout timer if a valid token already exists.
 *  - Cleans up an already-expired token.
 *  - Re-checks on tab focus (setTimeout can drift while the tab is suspended).
 */
export function initAdminSession() {
  const token = getAdminToken();
  if (token) armTimer(token);

  if (!visibilityListenerInstalled && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      const t = localStorage.getItem(TOKEN_KEY);
      if (!t) return;
      if (tokenExpiryMs(t) <= Date.now()) handleExpiry();
    });
    visibilityListenerInstalled = true;
  }
}

/** Convenience helper for components that perform admin API calls. */
export function adminAuthHeader() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
